import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import {
  Box,
  Drawer,
} from '@mui/material';

import CustomerHandling from '../CustomerHandling';
import permissions from '../../services/permissions';
import { IS_PRODUCTION } from '../../services/constants';
import useIsScrollVisible from '../../services/hooks/useIsScrollVisible';
import { NEXWAY, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_WITH0UT_SCROLL, SIDEBAR_WIDTH_WITH_SCROLL } from '../../constants/consts';

import HandleSearchSection from './HandleSearchSection';
import IncludesDefault from './HandleIncludesDefaultSection';
import HandleExpandCollapseSection from './HandleExpandCollapseSection';
import LogoSection from './LogoSection';
import NavListSection from './NavListSection';
import { useStyles } from './useStyles';
import './SideBar.scss';

const SideBar = ({ toggleDrawer, toggleScrollVisible, open }) => {
  const drawerRef = useRef(null);
  const allowedSideBarItems = useMemo(() => permissions.getAllowedSideBarItems(), []);
  const [inputValue, setInputValue] = useState('');
  const [curNavConfig, setCurNavConfig] = useState(allowedSideBarItems);
  const [isOpenAllSections, setOpenAllSections] = useState(null);
  const [scrollTarget, setScrollTarget] = useState(null);
  const isScrollVisible = useIsScrollVisible({
    root: scrollTarget,
    onToggleScrollVisible: toggleScrollVisible,
  });

  const drawerOpenWidth = isScrollVisible ? SIDEBAR_WIDTH_WITH_SCROLL : SIDEBAR_WIDTH_WITH0UT_SCROLL;
  const classes = useStyles(drawerOpenWidth, SIDEBAR_WIDTH_COLLAPSED);
  const {
    authorizedCustomerIds = [],
    customerId,
  } = JSON.parse(localStorage.getItem('decodedToken'));

  const includesNexway = [...authorizedCustomerIds, customerId].includes(NEXWAY);

  useEffect(() => {
    if (drawerRef && drawerRef.current) {
      setScrollTarget(drawerRef.current.children[0]);
    }
  }, []);

  const customerScope = useSelector(({
    account: { nexwayState },
  }) => nexwayState?.selectedCustomer?.id);

  const handleUpdateValue = (value) => {
    const searchValue = value.toLowerCase();
    setInputValue(value);

    if (value) {
      const newConfig = [];
      allowedSideBarItems.forEach((item) => {
        const newItems = [];

        item.items.forEach(((subItem) => {
          if (subItem.title.toLocaleLowerCase().includes(searchValue)) {
            newItems.push(subItem);
          }
        }));

        if (newItems.length) {
          newConfig.push({ ...item, items: newItems });
        }
      });

      setCurNavConfig(newConfig);
      setOpenAllSections(true);
    } else {
      setCurNavConfig(allowedSideBarItems);
      setOpenAllSections(false);
    }
  };

  const handleExpandAll = () => {
    setOpenAllSections(true);
  };

  const handleCollapseAll = () => {
    setOpenAllSections(false);
  };

  return (
    <>
      <Drawer
        anchor="left"
        open={open}
        variant="permanent"
        PaperProps={{
          style: {
            marginTop: IS_PRODUCTION ? 0 : '25px',
            height: '100vh',
          },
        }}
        className={classNames('side-bar', classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: classNames({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
        ref={drawerRef}
      >
        <Box height="fit-content" display="flex" flexDirection="column">
          <Box
            display="flex"
            flexDirection="column"
            className="side-nav"
            px={2}
          >
            <LogoSection open={open} toggleDrawer={toggleDrawer} />

            {/** box customer area */}
            <Box
              px="12px"
              py="12px"
              mx="-12px"
              className={classNames('side-nav-customer', {
                [classes.hide]: !open,
              })}
            >
              <Box mx="-5px"><CustomerHandling /></Box>
              <Box p="0 3px">
                {includesNexway && customerScope && customerScope !== NEXWAY && <IncludesDefault />}
              </Box>
            </Box>

            {/** search */}
            <Box
              mx="-5px"
              className={classNames({
                [classes.hide]: !open,
              })}
            >
              <HandleSearchSection
                inputValue={inputValue}
                setInputValue={setInputValue}
                handleUpdateValue={handleUpdateValue}
              />
            </Box>

            {/** expand collapse buttons */}
            <Box
              mx="-5px"
              py="6px"
              className={classNames({
                [classes.hide]: !open,
              })}
            >
              <HandleExpandCollapseSection
                handleExpandAll={handleExpandAll}
                handleCollapseAll={handleCollapseAll}
              />
            </Box>

            {/** side-nav-block */}
            <NavListSection
              curNavConfig={curNavConfig}
              isOpenAllSections={isOpenAllSections}
              setOpenAllSections={setOpenAllSections} />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

SideBar.propTypes = {
  toggleDrawer: PropTypes.func,
  toggleScrollVisible: PropTypes.func,
  open: PropTypes.bool,
};

export default SideBar;
